import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import type { PlaybookRunSummary } from "@/api/types";
import { DryRunBanner } from "@/components/DryRunBanner";
import { ModeBadge, RunStatusBadge } from "@/components/RunDetail";
import { Pagination } from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRun, useRuns } from "@/hooks/useRuns";
import { ChevronDown, ChevronRight } from "lucide-react";
import { formatDuration, relativeAge } from "@/lib/utils";

const PER_PAGE = 50;

export function RunsPage() {
  const [page, setPage] = useState(1);
  const { data, isPending, error } = useRuns({ page, perPage: PER_PAGE });
  const [expanded, setExpanded] = useState<string | null>(null);

  const errorMessage = error
    ? error instanceof ApiError
      ? error.message
      : "Error al cargar los runs."
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Historial de Autoremediaciones ejecutadas</h1>
        <p className="text-sm text-muted-foreground">
          Refresco automático cada 30 segundos.
        </p>
      </div>

      <DryRunBanner />

      {errorMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      {isPending && !data ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Cargando…
        </div>
      ) : data && data.items.length === 0 ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Sin runs registrados todavía.
        </div>
      ) : data ? (
        <>
          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead className="w-44">Inicio</TableHead>
                  <TableHead>SRP</TableHead>
                  <TableHead className="w-44">Incidente</TableHead>
                  <TableHead className="w-24">Modo</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-24 text-right">Duración</TableHead>
                  <TableHead className="w-28">Notificación</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((run) => (
                  <RunRow
                    key={run.run_id}
                    run={run}
                    isExpanded={expanded === run.run_id}
                    onToggle={() =>
                      setExpanded((cur) => (cur === run.run_id ? null : run.run_id))
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={data.page}
            perPage={data.per_page}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : null}
    </div>
  );
}

function RunRow({
  run,
  isExpanded,
  onToggle,
}: {
  run: PlaybookRunSummary;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          {isExpanded ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground tabular-nums">
          {relativeAge(run.started_at)}
        </TableCell>
        <TableCell className="font-mono text-xs">{run.playbook_id}</TableCell>
        <TableCell>
          <Link
            to={`/incidents/${run.incident_id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs text-primary underline-offset-2 hover:underline"
          >
            {run.incident_id.slice(0, 14)}…
          </Link>
        </TableCell>
        <TableCell>
          <ModeBadge mode={run.mode} />
        </TableCell>
        <TableCell>
          <RunStatusBadge status={run.status} />
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {formatDuration(run.duration_ms)}
        </TableCell>
        <TableCell>
          <NotificationBadge value={run.notification_status} />
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/20 p-3">
            <ExpandedRunSteps runId={run.run_id} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ExpandedRunSteps({ runId }: { runId: string }) {
  const { data, isPending, error } = useRun(runId);

  if (isPending) {
    return <div className="text-xs text-muted-foreground">Cargando steps…</div>;
  }
  if (error) {
    return (
      <div className="text-xs text-destructive">
        {error instanceof ApiError ? error.message : "Error cargando el run."}
      </div>
    );
  }
  if (!data) return null;
  return (
    <pre className="max-h-96 overflow-auto rounded bg-card p-3 text-xs leading-relaxed">
      <code>{JSON.stringify(data.steps, null, 2)}</code>
    </pre>
  );
}

function NotificationBadge({ value }: { value: string | null }) {
  if (value === "sent") {
    return <Badge className="bg-green-600 text-white hover:bg-green-600">sent</Badge>;
  }
  if (value === "failed") {
    return <Badge className="bg-red-600 text-white hover:bg-red-600">failed</Badge>;
  }
  if (value === "disabled") {
    return <Badge variant="secondary">disabled</Badge>;
  }
  return <Badge variant="outline">—</Badge>;
}
